import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, track } from "../lib/api";
import { useOutputLang } from "../context/OutputLangContext";
import { LanguageToggle } from "../components/LanguageToggle";
import { PaisleyDivider } from "../components/Motif";
import { Button } from "../components/ui/button";
import { Lock, ArrowRight } from "lucide-react";

const STR = {
  en: { free: "Your free reading", suffix: "Reading", theme: "Your primary theme", phase: "Your current phase",
    observ: "Standing out in your chart", more: "Unlock your full Vedic report",
    cta: (p) => `See my detailed report — ₹${p}`, once: "One-time payment • No subscription",
    locked: ["Full birth-chart structure", "Why this shows in your chart", "Mahadasha & Antardasha analysis", "Transits (Jupiter / Saturn / Rahu / Ketu)", "12–36 month timeline", "Supportive & demanding periods"] },
  hi: { free: "आपकी मुफ़्त रीडिंग", suffix: "रीडिंग", theme: "आपका मुख्य विषय", phase: "आपका वर्तमान दौर",
    observ: "आपकी कुंडली में उभरते बिंदु", more: "अपनी पूरी वैदिक रिपोर्ट अनलॉक करें",
    cta: (p) => `मेरी विस्तृत रिपोर्ट देखें — ₹${p}`, once: "एक बार का भुगतान • कोई सब्सक्रिप्शन नहीं",
    locked: ["पूरी जन्म-कुंडली संरचना", "यह आपकी कुंडली में क्यों है", "महादशा और अंतरदशा विश्लेषण", "गोचर (बृहस्पति / शनि / राहु / केतु)", "12–36 महीने की समयरेखा", "अनुकूल और चुनौतीपूर्ण दौर"] },
};

export default function Teaser() {
  const { readingId } = useParams();
  const navigate = useNavigate();
  const { lang } = useOutputLang();
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get(`/readings/${readingId}`).then(({ data }) => {
      setR(data);
      track("teaser_viewed", { reading_id: readingId, tool_id: data.tool_id });
      if (data.paid) navigate(`/reading/${readingId}`, { replace: true });
    }).catch(() => navigate("/"));
  }, [readingId]); // eslint-disable-line

  if (!r) return <div className="mx-auto max-w-2xl px-5 py-20"><div className="h-80 rounded-2xl bg-muted shimmer" /></div>;

  const s = STR[lang];
  const t = (lang === "hi" && r.teaser_hi) ? r.teaser_hi : r.teaser;

  const goPay = () => { track("paywall_viewed", { reading_id: readingId }); navigate(`/pay/${readingId}`); };

  return (
    <div className="mx-auto max-w-2xl px-5 pt-8 pb-32 md:pt-12 md:pb-12">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{s.free}</span>
        <LanguageToggle />
      </div>

      <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight md:text-4xl" data-testid="teaser-title">
        {lang === "hi" ? `आपकी ${r.category} ${s.suffix}` : `Your ${r.category} ${s.suffix}`}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{r.question}</p>

      <div className="card-soft mt-8 rounded-2xl p-6 fade-up">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.theme}</div>
        <div className="mt-1 font-display text-2xl font-semibold gold-text" data-testid="teaser-theme">{t.theme_title}</div>
        {t.phase && (
          <div className="mt-3 inline-block rounded-full bg-secondary px-3 py-1 text-sm font-medium">{t.phase}</div>
        )}
        <p className="mt-4 leading-relaxed text-muted-foreground">{t.theme_text || t.teaser_insight}</p>
      </div>

      {t.observations?.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.observ}</div>
          <div className="mt-3 space-y-3">
            {t.observations.map((o, i) => (
              <div key={i} className="card-soft rounded-2xl p-4" data-testid={`teaser-observation-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="font-display text-lg font-semibold gold-text">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="font-medium">{o.title}</div>
                    <p className="mt-0.5 text-sm text-muted-foreground">{o.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {t.mini_timeline?.length > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-3">
          {t.mini_timeline.map((m, i) => (
            <div key={i} className="card-soft rounded-2xl p-4 text-center">
              <div className="font-display text-sm font-semibold">{m.year}</div>
              <div className="mt-1 text-xs text-muted-foreground">{m.label}</div>
            </div>
          ))}
        </div>
      )}

      <PaisleyDivider />

      <div>
        <h2 className="font-display text-xl font-semibold">{s.more}</h2>
        <div className="mt-4 space-y-2.5">
          {s.locked.map((l) => (
            <div key={l} className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-card/60 px-4 py-3 text-sm text-muted-foreground">
              <Lock className="h-4 w-4 shrink-0" /> {l}
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-4 mt-10 rounded-full md:static">
        <Button data-testid="unlock-cta" onClick={goPay} className="w-full rounded-full bg-foreground py-6 text-base text-background btn-lift hover:bg-foreground shadow-lg md:shadow-none">
          {s.cta(r.price)} <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
        <p className="mt-2 text-center text-xs text-muted-foreground">{s.once}</p>
      </div>

      <div className="mt-8 space-y-1.5">
        {r.disclaimers.map((d, i) => <p key={i} className="text-xs text-muted-foreground">{d}</p>)}
      </div>
    </div>
  );
}
